/**
 * LLM 模型行与 token_tiered 价目表单的唯一转换点。
 * 表单金额字段使用人民币元/百万 tokens，提交时转换为 micros；末档 input_up_to 留空表示不封顶。
 */

const MODEL_MAX_LENGTH = 128
export const PRICING_MODES = ['token_tiered']

export function emptyModelForm() {
  return {
    model: '',
    enabled: true,
    params: '',
    mode: 'token_tiered',
    tiers: [{
      input_up_to: '',
      input_yuan_per_m: '',
      output_yuan_per_m: '',
      cached_input_yuan_per_m: '',
      cache_write_input_yuan_per_m: '',
    }],
  }
}

export function modelFormFromRow(row) {
  if (row.pricing.mode !== 'token_tiered') {
    throw new Error(`Unhandled pricing mode: ${row.pricing.mode}, id: ${row.id}`)
  }
  return {
    model: row.model,
    enabled: row.enabled,
    params: row.params === null ? '' : JSON.stringify(row.params, null, 2),
    mode: 'token_tiered',
    tiers: row.pricing.tiers.map((tier) => ({
      input_up_to: tier.input_up_to === null ? '' : String(tier.input_up_to),
      input_yuan_per_m: yuanFromMicros(tier.input_micros_per_m),
      output_yuan_per_m: yuanFromMicros(tier.output_micros_per_m),
      cached_input_yuan_per_m: yuanFromMicros(tier.cached_input_micros_per_m),
      cache_write_input_yuan_per_m: yuanFromMicros(tier.cache_write_input_micros_per_m),
    })),
  }
}

export function validateTiers(tiers) {
  if (tiers.length === 0) return { rows: [], rule: 'noTiers' }
  const rows = tiers.map((tier, index) => {
    const row = {}
    const isLast = index === tiers.length - 1
    if (isLast) {
      if (tier.input_up_to.trim() !== '') row.input_up_to = 'positiveInt'
    } else if (!isPositiveInt(tier.input_up_to)) {
      row.input_up_to = 'positiveInt'
    }
    for (const field of [
      'input_yuan_per_m',
      'output_yuan_per_m',
      'cached_input_yuan_per_m',
      'cache_write_input_yuan_per_m',
    ]) {
      if (!isNonNegativePrice(tier[field])) row[field] = 'nonNegPrice'
    }
    return row
  })
  const errors = { rows, rule: null }
  const last = tiers[tiers.length - 1]
  if (last.input_up_to.trim() !== '') {
    errors.rule = 'lastTierUnbounded'
    return errors
  }
  if (tiers.slice(0, -1).some((tier) => tier.input_up_to.trim() === '')) {
    errors.rule = 'unboundedOnlyLast'
    return errors
  }
  const bounds = tiers.slice(0, -1).map((tier) => tier.input_up_to)
  if (bounds.every(isPositiveInt)) {
    const numbers = bounds.map(Number)
    if (!numbers.every((value, index) => index === 0 || value > numbers[index - 1])) {
      errors.rule = 'strictlyIncreasing'
    }
  }
  return errors
}

export function validatePricingForm(form) {
  if (form.mode !== 'token_tiered') throw new Error(`Unhandled pricing mode: ${form.mode}`)
  return validateTiers(form.tiers)
}

export function validateModelForm(form) {
  const errors = {}
  if (form.model.trim() === '') errors.model = 'required'
  else if (form.model.length > MODEL_MAX_LENGTH) errors.model = 'tooLong'
  const params = parseParams(form.params)
  if (params.error) errors.params = params.error
  const pricing = validatePricingForm(form)
  const hasPricingError = pricing.rule !== null
    || pricing.rows.some((row) => Object.keys(row).length > 0)
  if (hasPricingError) errors.pricing = pricing
  return errors
}

export function toPricing(form) {
  if (form.mode !== 'token_tiered') throw new Error(`Unhandled pricing mode: ${form.mode}`)
  return {
    mode: 'token_tiered',
    tiers: form.tiers.map((tier) => ({
      input_up_to: tier.input_up_to.trim() === '' ? null : Number(tier.input_up_to),
      input_micros_per_m: microsFromYuan(tier.input_yuan_per_m),
      output_micros_per_m: microsFromYuan(tier.output_yuan_per_m),
      cached_input_micros_per_m: microsFromYuan(tier.cached_input_yuan_per_m),
      cache_write_input_micros_per_m: microsFromYuan(tier.cache_write_input_yuan_per_m),
    })),
  }
}

export function toModelSaveBody(form) {
  const errors = validateModelForm(form)
  const fields = Object.keys(errors)
  if (fields.length > 0) throw new Error(`invalid model form fields: ${fields.join(', ')}`)
  return {
    model: form.model.trim(),
    pricing: toPricing(form),
    params: parseParams(form.params).value,
    enabled: form.enabled,
  }
}

export function toModelEnabledBody(row, enabled) {
  return { model: row.model, pricing: row.pricing, params: row.params, enabled }
}

function parseParams(text) {
  if (text.trim() === '') return { value: null }
  try {
    const value = JSON.parse(text)
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
      return { value: null, error: 'notObject' }
    }
    return { value }
  } catch {
    return { value: null, error: 'invalidJson' }
  }
}

function isNonNegInt(value) {
  return /^\d+$/.test(value.trim())
}

function isPositiveInt(value) {
  return isNonNegInt(value) && Number(value) > 0
}

function isNonNegativePrice(value) {
  return /^\d+(\.\d{1,6})?$/.test(value.trim())
}

function microsFromYuan(value) {
  return Math.round(Number(value) * 1_000_000)
}

function yuanFromMicros(value) {
  return String(Number(value) / 1_000_000)
}
